import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowLeft, TrendingUp, Download } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { ChartTimeframeSelector } from './ChartTimeframeSelector'
import { EnhancedDataTable } from './EnhancedDataTable'
import { useDailyEmailAnalytics } from '@/hooks/useDailyEmailAnalytics'
import { supabase } from '@/integrations/supabase/client'

interface BaseDetailViewProps {
  config: {
    title: string
    description: string
    metricsCards: Array<{
      title: string
      value: string | number
      description: string
      icon: React.ReactNode
    }>
    chartTitle: string
    chartDescription: string
    detailsTable: {
      title: string
      description: string
      columns: Array<{
        header: string
        key: string
        render?: (value: any, record: any) => React.ReactNode
      }>
    }
    csvFileName: string
    fetchDetailRecords: (startDate: string, endDate: string) => Promise<any[]>
    realtimeChannelName?: string
    realtimeTables?: string[]
  }
  onBack: () => void
  onRecordClick?: (record: any) => void
}

export const BaseDetailView = ({ config, onBack, onRecordClick }: BaseDetailViewProps) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'today' | 'last7days' | 'last30days' | 'custom'>('today')
  const [detailRecords, setDetailRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  
  const { data: chartData, loading: chartLoading } = useDailyEmailAnalytics(selectedTimeframe)

  const fetchDetailRecords = async () => {
    try {
      setLoading(true)
      const endDate = new Date()
      let startDate = new Date()
      
      switch (selectedTimeframe) {
        case 'today':
          startDate.setHours(0, 0, 0, 0)
          endDate.setHours(23, 59, 59, 999)
          break
        case 'last7days':
          startDate.setDate(startDate.getDate() - 7)
          break
        case 'last30days':
          startDate.setDate(startDate.getDate() - 30)
          break
      }

      const records = await config.fetchDetailRecords(
        startDate.toISOString(),
        endDate.toISOString()
      )
      setDetailRecords(records)
    } catch (error) {
      console.error('Error fetching detail records:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDetailRecords()
  }, [selectedTimeframe])

  // Set up real-time subscription
  useEffect(() => {
    if (!config.realtimeChannelName || !config.realtimeTables) return

    const channel = supabase
      .channel(config.realtimeChannelName)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: config.realtimeTables[0] },
        () => {
          fetchDetailRecords()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [config.realtimeChannelName, config.realtimeTables]) // Removed selectedTimeframe to prevent subscription recreation

  const handleExport = () => {
    const csvContent = [
      config.detailsTable.columns.map(col => col.header).join(','),
      ...detailRecords.map(record => 
        config.detailsTable.columns.map(col => {
          const value = record[col.key]
          return typeof value === 'string' ? `"${value}"` : value || ''
        }).join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${config.csvFileName}-${selectedTimeframe}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const getChartData = () => {
    if (!chartData.length) return []
    
    if (selectedTimeframe === 'today') {
      return chartData[0]?.hourly_breakdown?.map(hour => ({
        name: `${hour.hour}:00`,
        value: hour.sent,
        replies: hour.replies,
        bounces: hour.bounces
      })) || []
    }
    
    return chartData.map(day => ({
      name: new Date(day.date).toLocaleDateString(),
      value: day.total_sent,
      replies: day.total_replies,
      bounces: day.total_bounces
    }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{config.title}</h1>
          <p className="text-muted-foreground">{config.description}</p>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {config.metricsCards.map((card, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
              {card.icon}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.value}</div>
              <p className="text-xs text-muted-foreground">{card.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Chart Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg">{config.chartTitle}</CardTitle>
              <CardDescription>{config.chartDescription}</CardDescription>
            </div>
            <ChartTimeframeSelector
              selectedTimeframe={selectedTimeframe}
              onTimeframeChange={setSelectedTimeframe}
            />
          </div>
        </CardHeader>
        <CardContent>
          {chartLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="hsl(var(--primary))" name="Sent" />
                <Bar dataKey="replies" fill="hsl(var(--success))" name="Replies" />
                <Bar dataKey="bounces" fill="hsl(var(--destructive))" name="Bounces" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Data Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg">{config.detailsTable.title}</CardTitle>
              <CardDescription>{config.detailsTable.description}</CardDescription>
            </div>
            <Badge variant="outline" className="text-xs">
              {selectedTimeframe === 'today' ? 'Live Updates' : 'Historical Data'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <EnhancedDataTable
            columns={config.detailsTable.columns}
            data={detailRecords}
            onRecordClick={onRecordClick}
            onExport={handleExport}
            loading={loading}
            searchPlaceholder="Search email records..."
            totalRecords={detailRecords.length}
            filterOptions={[
              {
                key: 'event_type',
                label: 'Event Type',
                options: [
                  { value: 'EMAIL_SENT', label: 'Sent' },
                  { value: 'EMAIL_REPLY', label: 'Reply' },
                  { value: 'EMAIL_BOUNCE', label: 'Bounce' }
                ]
              }
            ]}
          />
        </CardContent>
      </Card>
    </div>
  )
}